/**
 * Sandbox Group Selector dropdown.
 * Shows the currently active sandbox group scope and lets the user switch groups
 * or return to the default (all-resources) view.
 *
 * When a group is selected, navigating to Sandboxes / Disk Images / Snapshots
 * automatically uses the group-scoped path.
 *
 * @module components/layout/SandboxGroupSelector
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, useSearchParams } from 'react-router';
import { ChevronDown, Layers, Plus, Check, List } from 'lucide-react';
import { clsx } from 'clsx';
import { useSandboxGroups } from '@/hooks';

/** Resources that support a group-scoped URL path. */
const GROUP_SCOPED_SEGMENTS: Record<string, string> = {
  '/sandboxes': 'sandboxes',
  '/disk-images': 'disk-images',
  '/snapshots': 'snapshots',
  '/secrets': 'secrets',
  '/volumes': 'volumes',
  '/connectors': 'connectors',
  '/content-packages': 'content-packages',
};

/** Extract the active group ID from the current URL, if any. */
function getActiveGroupIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/sandbox-groups\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Given the current path and a new group ID, compute the target URL.
 * - If the current path is a group-scoped page, switch to the same resource under the new group.
 * - If the current path is a plain resource page (/sandboxes, /disk-images, /snapshots), scope it.
 * - Otherwise default to group's sandboxes page.
 */
function buildGroupPath(pathname: string, groupId: string): string {
  const encoded = encodeURIComponent(groupId);

  // Already in a group-scoped path: swap out the group ID, keep the resource segment
  const groupMatch = pathname.match(/^\/sandbox-groups\/[^/]+\/([^/]+)/);
  if (groupMatch) {
    return `/sandbox-groups/${encoded}/${groupMatch[1]}`;
  }

  // On a scopeable top-level resource page: redirect to its group-scoped version
  for (const [baseHref, segment] of Object.entries(GROUP_SCOPED_SEGMENTS)) {
    if (pathname === baseHref || pathname.startsWith(baseHref + '/')) {
      return `/sandbox-groups/${encoded}/${segment}`;
    }
  }

  // Default fallback
  return `/sandbox-groups/${encoded}/sandboxes`;
}

/**
 * Given the current path, compute the "default" (no-group) target URL.
 * - If the current path is a group-scoped page, return the equivalent plain resource page.
 * - Otherwise stay where we are.
 */
function buildDefaultPath(pathname: string): string {
  const groupMatch = pathname.match(/^\/sandbox-groups\/[^/]+\/([^/]+)/);
  if (groupMatch) {
    const segment = groupMatch[1];
    for (const [baseHref, seg] of Object.entries(GROUP_SCOPED_SEGMENTS)) {
      if (seg === segment) return baseHref;
    }
    return '/sandboxes';
  }
  return pathname;
}

/**
 * Dropdown button in the top header that lets users select a sandbox group scope.
 */
export function SandboxGroupSelector() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const { data: groups } = useSandboxGroups(1, 100);

  // Active group comes from the URL path (group-scoped routes) or a ?groupId= query param (create pages)
  const activeGroupId = getActiveGroupIdFromPath(pathname) ?? searchParams.get('groupId');

  const openDropdown = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 6, left: rect.left });
    }
    setOpen(true);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        !buttonRef.current?.contains(e.target as Node) &&
        !dropdownRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (groupId: string | null) => {
    setOpen(false);
    if (groupId === null) {
      navigate(buildDefaultPath(pathname));
    } else {
      navigate(buildGroupPath(pathname, groupId));
    }
  };

  const label = activeGroupId ?? 'Default';

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => (open ? setOpen(false) : openDropdown())}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Sandbox group scope: ${label}`}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#161616] text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-[#3a3a3a] transition-colors"
      >
        <Layers className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <span className="max-w-[8rem] truncate font-medium">{label}</span>
        <ChevronDown
          className={clsx(
            'h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform duration-150',
            open && 'rotate-180'
          )}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            role="listbox"
            aria-label="Select sandbox group"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
            className="fixed w-64 bg-white dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl z-[200] overflow-hidden"
          >
            <div className="py-1">
              {/* Default / all-resources option */}
              <button
                role="option"
                aria-selected={!activeGroupId}
                onClick={() => handleSelect(null)}
                className={clsx(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left',
                  !activeGroupId
                    ? 'bg-gray-50 dark:bg-[#1e1e1e] text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1e1e1e]'
                )}
              >
                {!activeGroupId ? (
                  <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />
                ) : (
                  <span className="w-4 flex-shrink-0" />
                )}
                <span className="font-medium">Default</span>
                <span className="ml-auto text-xs text-gray-400">All resources</span>
              </button>

              {/* Separator + group list */}
              {groups && groups.length > 0 && (
                <>
                  <div className="border-t border-gray-100 dark:border-[#2a2a2a] my-1" />
                  <div className="px-3 py-1">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                      Groups
                    </span>
                  </div>
                  {groups.map((group) => (
                    <button
                      key={group.id}
                      role="option"
                      aria-selected={activeGroupId === group.id}
                      onClick={() => handleSelect(group.id)}
                      className={clsx(
                        'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left',
                        activeGroupId === group.id
                          ? 'bg-gray-50 dark:bg-[#1e1e1e] text-gray-900 dark:text-white'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1e1e1e]'
                      )}
                    >
                      {activeGroupId === group.id ? (
                        <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />
                      ) : (
                        <span className="w-4 flex-shrink-0" />
                      )}
                      <span className="truncate">{group.id}</span>
                    </button>
                  ))}
                </>
              )}

              {/* Create new group */}
              <div className="border-t border-gray-100 dark:border-[#2a2a2a] my-1" />
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/sandbox-groups/new');
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-[#1e1e1e] transition-colors text-left"
              >
                <Plus className="h-4 w-4 flex-shrink-0" />
                <span>Create Group</span>
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/sandbox-groups');
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1e1e1e] transition-colors text-left"
              >
                <List className="h-4 w-4 flex-shrink-0" />
                <span>List All Groups</span>
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
